import React from 'react';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import CreatorProgramTable from '../components/CreatorProgramTable';
import { formatDateAndTime } from '../../../utils/formatDateTime';
import { EMPTY_STATES, FIELD_LIMITS } from '../constants';
import { formatCurrency } from '../utils/formatters';
import { formatAllowance, sortTermsByStart } from '../utils/terms';
import { SectionTitle } from '../Styles';
import type { CreatorTerms } from '../types';

interface TermsHistoryTableProps {
  terms?: CreatorTerms[];
  currentTermsId?: string;
  loading?: boolean;
}

const formatDate = (iso?: string): string =>
  iso ? formatDateAndTime(iso).formattedDate : '—';

/**
 * Full terms version history.
 *
 * Terms are immutable — each row is a version that was current for some window, so
 * the history is the audit trail. The active row is highlighted; everything else is
 * either superseded or outside its date window.
 */
const TermsHistoryTable: React.FC<TermsHistoryTableProps> = ({
  terms,
  currentTermsId,
  loading,
}) => {
  const rows = sortTermsByStart(terms);

  const columns: ColumnsType<CreatorTerms> = [
    {
      title: 'VERSION',
      key: 'version',
      width: 130,
      render: (_, record) =>
        record.id === currentTermsId ? (
          <Tag color="success">Current</Tag>
        ) : record.supersededById ? (
          <Tag>Superseded</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    {
      title: 'ALLOWANCE',
      key: 'allowance',
      width: 130,
      render: (_, record) => formatAllowance(record),
    },
    {
      title: 'PERIOD',
      key: 'period',
      width: 220,
      render: (_, record) => `${formatDate(record.startsAt)} → ${formatDate(record.endsAt)}`,
    },
    {
      title: 'MIN SUBTOTAL',
      key: 'minSubtotal',
      width: 140,
      render: (_, record) =>
        record.minSubtotal != null ? formatCurrency(record.minSubtotal) : '—',
    },
    {
      title: 'MAX CODES',
      key: 'maxActiveCodes',
      width: 120,
      render: (_, record) => record.maxActiveCodes ?? FIELD_LIMITS.DEFAULT_MAX_ACTIVE_CODES,
    },
    {
      title: 'STORES',
      key: 'stores',
      width: 130,
      render: (_, record) =>
        !record.storeIds || record.storeIds.length === 0
          ? 'All stores'
          : `${record.storeIds.length} store(s)`,
    },
  ];

  return (
    <>
      <SectionTitle>Terms History</SectionTitle>
      <CreatorProgramTable<CreatorTerms>
        variant="nested"
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        emptyText={EMPTY_STATES.TERMS}
      />
    </>
  );
};

/**
 * Memoised: re-renders only when its own props change, not whenever the parent
 * screen re-renders for an unrelated filter or fetch.
 */
export default React.memo(TermsHistoryTable);
